import React from 'react'
import { useTranslation } from 'react-i18next'
import { formatFocusDuration } from '@/src/renderer/lib/focus'
import { DecorativeWave } from '@/src/renderer/components/ui/DecorativeWave'
import type { DayLabel } from '../../utils/doneBuckets'
import './DoneDayGroup.scss'

export interface DoneDayGroupProps {
  label: DayLabel
  /** Wie viele Einträge unter der Überschrift stehen. */
  count: number
  /**
   * Die **Fokuszeit dieses Tages** in Sekunden — die Summe über seine Einträge.
   *
   * Steht neben der Zählung: erst wie viele, dann wie lange. Fehlt sie oder
   * bleibt sie unter einer Minute, steht dort nichts — an einem Tag ohne Fokus
   * soll keine Null stehen.
   */
  focusedSeconds?: number | null
  children: React.ReactNode
}

/**
 * Ein Tag auf **Geschafft**: Überschrift, Zählung, die Welle bis zum
 * rechten Rand — darunter die Einträge des Tages.
 *
 * Die Überschrift heißt „Heute", „Gestern", fünf Tage lang der Wochentag
 * allein und ab sieben Tagen zurück das Datum (CONTEXT.md, Geschafft).
 *
 * @example
 * <DoneDayGroup label={{ kind: 'today' }} count={5}>…</DoneDayGroup>
 */
export const DoneDayGroup: React.FC<DoneDayGroupProps> = ({ label, count, focusedSeconds, children }) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'en' ? 'en-US' : 'de-DE'

  const title = (() => {
    switch (label.kind) {
      case 'today': return t('done.day.today')
      case 'yesterday': return t('done.day.yesterday')
      case 'weekday': return label.date.toLocaleDateString(locale, { weekday: 'long' })
      case 'date': return label.date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long' })
    }
  })()

  return (
    <section className="done-day-group" aria-label={title}>
      <header className="done-day-group__header">
        <h2 className="done-day-group__title">{title}</h2>
        <span className="done-day-group__count">{t('done.day.count', { count })}</span>
        {/* Erst wie viele, dann wie lange. An einem Tag ohne Fokus steht hier
            nichts — eine Null wäre ein Vorwurf. */}
        {typeof focusedSeconds === 'number' && focusedSeconds > 0 && (
          <span className="done-day-group__focused">
            {t('done.day.focused', { duration: formatFocusDuration(focusedSeconds, t) })}
          </span>
        )}
        <DecorativeWave className="done-day-group__wave" />
      </header>
      <div className="done-day-group__entries">{children}</div>
    </section>
  )
}
